import {useEffect, useMemo, useState} from "react";
import {Alert, AlertTitle} from "@/primitives/Alert";
import {Button} from "@/primitives/Button";
import {FA6Icon} from "@/primitives/FA6Icon";
import {createDataTableColumnHelper, DataTable, useDataTable} from "@/primitives/DataTable";
import {Input} from "@/primitives/Input";
import {InputGroup} from "@/primitives/InputGroup";
import {api} from "@/shared/api";
import type {Group} from "@/shared/timr";
import {buildCorrectAbsenceCalendarUrl} from "@/shared/utils/misc";
import {CalendarLinkActions} from "@/components/calendarLinkActions";
import {CalendarLinkRow} from "@/components/calendarLinkRow";
import {SecretInputGroup} from "@/components/SecretInputGroup";

const FILTER_DEBOUNCE_MS = 350;

/* Same box the legacy group table scrolled in (subscribeAbsenceCalendarComponent). */
const GROUP_LIST_HEIGHT = 350;

export interface AbsenceCalendarLinksProps {
	initialToken: string | null;
	showTeammemberAbsenceCalendar: boolean;
	showAllAbsenceCalendar: boolean;
	timrBaseUrl: string;
	/**
	 * Reports a generated, renewed or deactivated token to the host. Needed wherever the
	 * component is remounted from outside state — a modal reopened after a renewal would
	 * otherwise hand back the token it was first constructed with.
	 */
	onTokenChange?: (token: string | null) => void;
	/**
	 * Reports whether a token request is in flight, so a host that can unmount this component
	 * (a modal) can refuse to close: the response would land on the unmounted instance and the
	 * next mount would seed itself from a token the server has meanwhile rotated away.
	 */
	onBusyChange?: (busy: boolean) => void;
}

/**
 * Subscription links for the absence calendars, with the group links behind a filter.
 * Chrome-free on purpose: the settings page frames it with a panel, the requests page with a
 * modal. Brings its own `form-horizontal` because the link rows are horizontal form rows.
 */
export function AbsenceCalendarLinks({
	initialToken,
	showTeammemberAbsenceCalendar,
	showAllAbsenceCalendar,
	timrBaseUrl,
	onTokenChange,
	onBusyChange,
}: AbsenceCalendarLinksProps) {
	const [token, setToken] = useState(initialToken);
	const [busy, setBusy] = useState(false);

	const applyBusy = (pending: boolean) => {
		setBusy(pending);
		onBusyChange?.(pending);
	};

	const applyToken = (newToken: string | null) => {
		setToken(newToken);
		onTokenChange?.(newToken);
	};

	const generate = () => {
		applyBusy(true);
		api("generateAbsenceCalendarSubscriptionToken.json")
			.post()
			.done((response) => applyToken(response.calendarSubscriptionToken))
			.always(() => applyBusy(false));
	};

	const remove = () => {
		applyBusy(true);
		api("removeAbsenceCalendarSubscriptionToken.json")
			.post()
			.done(() => applyToken(null))
			.always(() => applyBusy(false));
	};

	return (
		<div className="form-horizontal">
			{token == null ? (
				<>
					<p className="content-group">
						{messages["user.settings.calendar.privateUrl.info"]}
					</p>
					<div className="text-right">
						<Button onClick={generate} disabled={busy}>
							<FA6Icon icon="link" variant="regular" fixedWidth className="mr-5"/>
							{messages["user.settings.calendar.privateUrl.generate"]}
						</Button>
					</div>
				</>
			) : (
				<>
					<p className="content-group">
						{messages["user.settings.absenceCalendar.desc"]}
					</p>
					<Alert variant="danger" iconPosition="left">
						<AlertTitle>{messages["dialog.warning.title"]}</AlertTitle>
						{messages["user.settings.absenceCalendar.privateUrl.warning"]}
					</Alert>
					{globals.workingTimeEnabled && (
						<>
							<CalendarLinkRow
								label={messages["user.settings.calendar.myAbsences"]}
								url={buildCorrectAbsenceCalendarUrl(token, timrBaseUrl, "my-absences.ics")}
							/>
							{showTeammemberAbsenceCalendar && (
								<CalendarLinkRow
									label={messages["user.settings.calendar.teammatesAbsences"]}
									url={buildCorrectAbsenceCalendarUrl(token, timrBaseUrl, "teammember-absences.ics")}
								/>
							)}
							{showAllAbsenceCalendar && (
								<CalendarLinkRow
									label={messages["user.settings.calendar.allAbsences"]}
									url={buildCorrectAbsenceCalendarUrl(token, timrBaseUrl, "all-absences.ics")}
								/>
							)}
							{showTeammemberAbsenceCalendar && (
								<GroupCalendarLinks token={token} timrBaseUrl={timrBaseUrl}/>
							)}
						</>
					)}
					<CalendarLinkActions
						calendarName={messages["admin.clientSettings.absenceCalendar"]}
						busy={busy}
						onRenew={generate}
						onDeactivate={remove}
					/>
				</>
			)}
		</div>
	);
}

/* Group breadcrumbs without the trailing group name; zero-width space after each slash allows line breaks. */
function groupPath(group: Group): string {
	const breadCrumbs = group.breadCrumbs ?? "";
	return breadCrumbs.substring(0, breadCrumbs.length - group.name.length).replaceAll("/", "/\u200B");
}

const columnHelper = createDataTableColumnHelper<Group>();

function GroupCalendarLinks({token, timrBaseUrl}: {token: string; timrBaseUrl: string}) {
	const [filter, setFilter] = useState("");
	const [groups, setGroups] = useState<Group[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let stale = false;
		setLoading(true);
		const timeout = window.setTimeout(() => {
			api("/settings/filterGroupsForAbsenceCalendar.json")
				.get({name: filter})
				.done((response) => {
					if (!stale) setGroups(response.data);
				})
				.always(() => {
					if (!stale) setLoading(false);
				});
		}, filter === "" ? 0 : FILTER_DEBOUNCE_MS);
		return () => {
			stale = true;
			window.clearTimeout(timeout);
		};
	}, [filter]);

	const columns = useMemo(
		() =>
			columnHelper.columns([
				columnHelper.accessor("name", {
					header: messages["admin.groupTree.header"],
					enableSorting: false,
					size: 220,
					cell: ({row}) => (
						<div>
							<div>{row.original.name}</div>
							{/* kept as a blank line for root groups so every row is the same height */}
							<div className="text-grey-600">{groupPath(row.original) || "\u00A0"}</div>
						</div>
					),
				}),
				columnHelper.display({
					id: "url",
					header: messages["user.settings.calendar.url"],
					cell: ({row}) => (
						<SecretInputGroup
							value={buildCorrectAbsenceCalendarUrl(token, timrBaseUrl, `group-${row.original.groupId}-absences.ics`)}
						/>
					),
				}),
			]),
		[token, timrBaseUrl],
	);

	/* every matching group at once inside a scroll container, like the legacy table */
	const table = useDataTable<Group>({
		data: groups,
		columns,
		getRowId: (group) => String(group.id),
	});

	/* the panel's 1px border is the frame the legacy group list sat in; it brings its own bottom margin */
	return (
		<div className="panel panel-flat mt-15">
			<div className="pt-15 pl-20 pr-20">
				<InputGroup.Root>
					<InputGroup.Addon square>
						<FA6Icon icon="search" variant="regular"/>
					</InputGroup.Addon>
					<Input
						type="search"
						value={filter}
						onChange={(event) => setFilter(event.target.value)}
						placeholder={messages["admin.groupTree.filter"]}
						aria-label={messages["admin.groupTree.filter"]}
					/>
				</InputGroup.Root>
			</div>
			<DataTable.Root table={table} loading={loading}>
				<DataTable.Content<Group> size="sm" layout="fixed" scrollHeight={GROUP_LIST_HEIGHT}/>
			</DataTable.Root>
		</div>
	);
}
